import React from "react"
import { Button, Modal, Table, Spinner, Col, Form, Row, OverlayTrigger, Popover } from "react-bootstrap"
import { toast } from "react-toastify"
import { connect } from "react-redux"
import { concat, of, Subject, Subscription, forkJoin } from "rxjs"
import { catchError, map, switchMap } from "rxjs/operators"
import ActionButton from "../ActionButton"
import { AppPageWrapper } from "../NavMenu/AppPageWrapper"
import { RouteComponentProps } from "react-router"
import { Home } from "../../config/Routes"
import Checkbox from "../Checkbox"
import { APIError } from "../../api/ObservableFromFetch"
import { AppState, WorkforceClinicPortal } from "../../AppState"
import { isPortalAdmin, isSiteAdmin } from "../../authentication/isAdminRole"
import { GetSelectedPortal, getUserNameForSelectedCompany } from "../../authentication/SelectedCompany/CompanySelectionDrivenSelectors"
import { Typeahead } from "react-bootstrap-typeahead"
import { FaAngleDown, FaInfoCircle } from "react-icons/fa"
import FormatError from "../../formatters/FormatError"
import ImplementationLabResultsReporting from "../../api/Implementations/Model/ImplementationLabResultsReporting"
import AccountLabResultsReporting from "../../api/Companies/CompanyModel/AccountLabResultsReporting"
import ImplementationLabResultsReportingGet, { ImplementationLabResultReportingGet, SetImplementationLabResultsReporting } from "../../api/Implementations/ImplementationLabResultsReporting"
import AccountLabResultsReportingGet, { SetAccountLabResultsReporting } from "../../api/Companies/AccountLabResultsReporting"

interface LabResultsReportingModalAuthProps {
  isPortalAdmin?: boolean
  isSiteAdmin?: boolean
  UserName?: string
  Portal?: WorkforceClinicPortal
}

interface LabResultsReportingModalProps { }

interface LabResultsReportingModalState {
  saving: boolean
  loading: boolean
  portals: ImplementationLabResultsReporting[]
  companies: AccountLabResultsReporting[]
  selectedPortal?: ImplementationLabResultsReporting
  selectedCompany?: AccountLabResultsReporting
  MRONonDOTNeg: boolean
  MRONonDOTPos: boolean
  POCTRequireLab: boolean
  POCTRequireMRO: boolean
}



export class LabResultsReportingModal extends React.PureComponent<LabResultsReportingModalProps & LabResultsReportingModalAuthProps, LabResultsReportingModalState> {
  emptyState: LabResultsReportingModalState = {
    loading: false,
    saving: false,
    portals: [],
    companies: [],
    selectedPortal: undefined,
    selectedCompany: undefined,
    MRONonDOTNeg: false,
    MRONonDOTPos: false,
    POCTRequireLab: false,
    POCTRequireMRO: false
  }
  state: LabResultsReportingModalState = { ...this.emptyState }

  portalTypeahead = React.createRef<Typeahead<ImplementationLabResultsReporting>>()
  companyTypeahead = React.createRef<Typeahead<AccountLabResultsReporting>>()
  loadPortalsSubscription?: Subscription
  loadPortalSubscription?: Subscription
  loadCompaniesSubscription?: Subscription
  saveSubscription?: Subscription

  componentWillMount() {
    this.setState({ loading: true, selectedPortal: undefined, portals: [], selectedCompany: undefined, companies: [] })
    if (this.props.isSiteAdmin) {
      this.loadPortalsSubscription = ImplementationLabResultsReportingGet().subscribe(
        data => {
          this.setState({ portals: data, loading: false })
        },
        error => this.handleAPIError(error, { loading: false })
      )
    }
    if (this.props.isPortalAdmin) {
      this.loadPortalSubscription = ImplementationLabResultReportingGet(this.props.Portal?.ImplementationID || 0).subscribe(
        data => {
          const implementationId = data?.ImplementationID || 0
          if (implementationId > 0) {
            this.loadCompaniesSubscription = AccountLabResultsReportingGet(implementationId).subscribe(
              companies => {
                this.setState({ selectedPortal: data, companies: companies, loading: false })
              },
              error => this.handleAPIError(error, { loading: false })
            )
          }
          else {
            this.setState({ selectedPortal: data, loading: false })
          }
        },
        error => this.handleAPIError(error, { loading: false })
      )
    }
  }

  componentWillUnmount() {
    this.loadPortalsSubscription && this.loadPortalsSubscription.unsubscribe()
    this.loadPortalSubscription && this.loadPortalSubscription.unsubscribe()
    this.loadCompaniesSubscription && this.loadCompaniesSubscription.unsubscribe()
    this.saveSubscription && this.saveSubscription.unsubscribe()
  }
  handleCancel = () => {
    (this.props as RouteComponentProps).history?.push(Home)
  }

  handleSave = () => {
    this.saveSubscription && this.saveSubscription.unsubscribe()
    const { isSiteAdmin, isPortalAdmin } = this.props
    if (!isSiteAdmin && !isPortalAdmin) {
      return
    }
    const { saving, loading, selectedPortal, selectedCompany } = this.state
    if (saving || loading) {
      return
    }
    if (!selectedPortal) {
      return
    }
    if (isSiteAdmin || (isPortalAdmin && !selectedCompany)) {
      this.setState({ saving: true })
      const saveObj = {
        ImplementationID: selectedPortal.ImplementationID,
        ImplementationName: selectedPortal.ImplementationName,
        MRONonDOTNeg: this.state.MRONonDOTNeg,
        MRONonDOTPos: this.state.MRONonDOTPos,
        POCTRequireLab: this.state.POCTRequireLab,
        POCTRequireMRO: this.state.POCTRequireMRO,
        UpdateUserLoginID: selectedPortal.UpdateUserLoginID,
        UpdateMethodID: selectedPortal.UpdateMethodID
      }
      this.saveSubscription = SetImplementationLabResultsReporting(saveObj).subscribe(
        (data) => {
          selectedPortal.MRONonDOTNeg = saveObj.MRONonDOTNeg
          selectedPortal.MRONonDOTPos = saveObj.MRONonDOTPos
          selectedPortal.POCTRequireLab = saveObj.POCTRequireLab
          selectedPortal.POCTRequireMRO = saveObj.POCTRequireMRO
          this.setState({ saving: false, selectedPortal: selectedPortal })
          toast.success(data)
        },
        (error) => this.handleAPIError(error, { saving: false })
      )
      return
    }
    if (isPortalAdmin && selectedCompany) {
      this.setState({ saving: true })
      const saveObj = {
        AccountID: selectedCompany.AccountID,
        ImplementationID: selectedCompany.ImplementationID,
        Code: selectedCompany.Code || "",
        Name: selectedCompany.Name,
        MRONonDOTNeg: this.state.MRONonDOTNeg,
        MRONonDOTPos: this.state.MRONonDOTPos,
        POCTRequireLab: this.state.POCTRequireLab,
        POCTRequireMRO: this.state.POCTRequireMRO,
        UpdateUserLoginID: selectedCompany.UpdateUserLoginID,
        UpdateMethodID: selectedCompany.UpdateMethodID
      }
      this.saveSubscription = SetAccountLabResultsReporting(saveObj).subscribe(
        (data) => {
          selectedCompany.MRONonDOTNeg = saveObj.MRONonDOTNeg
          selectedCompany.MRONonDOTPos = saveObj.MRONonDOTPos
          selectedCompany.POCTRequireLab = saveObj.POCTRequireLab
          selectedCompany.POCTRequireMRO = saveObj.POCTRequireMRO
          this.setState({ saving: false, selectedCompany: selectedCompany })
          toast.success(data)
        },
        (error) => this.handleAPIError(error, { saving: false })
      )
      return
    }
  }

  handleAPIError = (error: Error | APIError, stateChanges: object) => {
    this.setState(stateChanges)
    error && toast.error(FormatError(error))
  }

  handlePortalChange = (selected: ImplementationLabResultsReporting[]) => {
    const selectedPortal = selected[0]
    const mroNonDOTNeg = selectedPortal?.MRONonDOTNeg || false
    const mroNonDOTPos = selectedPortal?.MRONonDOTPos || false
    const poctRequireLab = selectedPortal?.POCTRequireLab || false
    const poctRequireMRO = selectedPortal?.POCTRequireMRO || false
    this.setState({
      selectedPortal: selectedPortal,
      selectedCompany: undefined,
      companies: [],
      MRONonDOTNeg: mroNonDOTNeg,
      MRONonDOTPos: mroNonDOTPos,
      POCTRequireLab: poctRequireLab,
      POCTRequireMRO: poctRequireMRO
    })
  }

  handleEmptyPortal = (company: string) => {
  }

  handleCompanyChange = (selected: AccountLabResultsReporting[]) => {
    const selectedPortal = this.state.selectedPortal
    const selectedCompany = selected[0]
    const mroNonDOTNeg = selectedCompany?.MRONonDOTNeg || selectedPortal?.MRONonDOTNeg || false
    const mroNonDOTPos = selectedCompany?.MRONonDOTPos || selectedPortal?.MRONonDOTPos || false
    const poctRequireLab = selectedCompany?.POCTRequireLab || selectedPortal?.POCTRequireLab || false
    const poctRequireMRO = selectedCompany?.POCTRequireMRO || selectedPortal?.POCTRequireMRO || false
    this.setState({
      selectedCompany: selectedCompany,
      MRONonDOTNeg: mroNonDOTNeg,
      MRONonDOTPos: mroNonDOTPos,
      POCTRequireLab: poctRequireLab,
      POCTRequireMRO: poctRequireMRO
    })
  }

  handleEmptyCompany = (company: string) => {
  }


  toggleMRONONDOTNeg = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target
    this.setState({ MRONonDOTNeg: target.checked })
  }

  toggleMRONONDOTPos = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target
    this.setState({ MRONonDOTPos: target.checked })
  }

  togglePOCTReqLabConf = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target
    this.setState({ POCTRequireLab: target.checked })
  }

  togglePOCTReqMRO = (event: React.ChangeEvent<HTMLInputElement>) => {
    const target = event.target
    this.setState({ POCTRequireMRO: target.checked })
  }

  render() {
    const { saving, loading, selectedPortal, portals, selectedCompany, companies } = this.state
    const { isSiteAdmin, isPortalAdmin } = this.props
    const mroNonDOTNeg = this.state.MRONonDOTNeg
    const mroNonDOTPos = this.state.MRONonDOTPos
    const poctRequireLab = this.state.POCTRequireLab
    const poctRequireMRO = this.state.POCTRequireMRO


    return (
      <AppPageWrapper showNav>
        <Modal.Dialog size="lg">
          <Modal.Header>
            <Modal.Title className="text-uppercase">Lab Results Reporting</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {isSiteAdmin &&
              <Form.Group as={Row}>
                <Col>
                  <Typeahead id="portal"
                    ref={this.portalTypeahead}
                    labelKey="ImplementationName"
                    filterBy={(option, props) => props.selected.length > 0 || option.ImplementationName.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                    isLoading={loading}
                    onChange={this.handlePortalChange}
                    onInputChange={text => this.handleEmptyPortal(text)}
                    disabled={loading}
                    options={portals || []}
                    selected={selectedPortal ? [selectedPortal || ""] : []}
                    placeholder="Select Portal"
                    emptyLabel="No Portals Found">
                    {!loading && <Button variant="toggle" onClick={() => { this.portalTypeahead.current?.toggleMenu() }}>
                      <FaAngleDown />
                    </Button>}
                  </Typeahead>
                </Col>
              </Form.Group>
            }
            {(isPortalAdmin && selectedPortal) &&
              <Form.Group as={Row}>
                <Col>
                  <Typeahead id="company"
                    ref={this.companyTypeahead}
                    labelKey="Name"
                    filterBy={(option, props) => props.selected.length > 0 || option.Name.toLowerCase().indexOf(props.text.toLowerCase()) !== -1}
                    isLoading={loading}
                    onChange={this.handleCompanyChange}
                    onInputChange={text => this.handleEmptyCompany(text)}
                    disabled={loading}
                    options={companies || []}
                    selected={selectedCompany ? [selectedCompany || ""] : []}
                    placeholder="Select Company"
                    emptyLabel="No Companies Found">
                    {!loading && <Button variant="toggle" onClick={() => { this.companyTypeahead.current?.toggleMenu() }}>
                      <FaAngleDown />
                    </Button>}
                  </Typeahead>
                </Col>
              </Form.Group>
            }
            <Form.Group as={Row}>
              <Table responsive bordered className="dark text-center">
                {this.state.loading &&
                  <tbody>
                    <tr><td colSpan={2}><Spinner animation="border" size="sm" /></td></tr>
                  </tbody>
                }
                {(!this.state.loading && selectedPortal) &&
                  <tbody>
                    <tr><th>For non-federal lab-based testing:</th><th></th></tr>
                    <tr key='trMRONonDOTNeg'><td>Require MRO for negative tests</td><td><Checkbox id='cbMRONonDOTNeg' checked={mroNonDOTNeg} onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.toggleMRONONDOTNeg(e)} className="mr-auto" /></td></tr>
                    <tr key='trMRONonDOTPos'><td>Require MRO for non-negative tests</td><td><Checkbox id='cbMRONonDOTPoss' checked={mroNonDOTPos} onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.toggleMRONONDOTPos(e)} className="mr-auto" /></td></tr>
                    <tr><th>For POCT or 'instant' testing:</th><th></th></tr>
                    <tr key='trPOCTReqLabConf'><td>Require laboratory confirmation for non-negative screens</td><td><Checkbox id='cbPOCTReqLabConf' checked={poctRequireLab} onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.togglePOCTReqLabConf(e)} className="mr-auto" /></td></tr>
                    <tr key='trPOCTReqMRO'><td>Require MRO for laboratory non-negative confirmations</td><td><Checkbox id='cbPOCTReqMRO' checked={poctRequireMRO} onChange={(e: React.ChangeEvent<HTMLInputElement>) => this.togglePOCTReqMRO(e)} className="mr-auto" /></td></tr></tbody>
                }

              </Table>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <ActionButton variant="primary" onClick={() => this.handleSave()} disabled={loading || saving} spin={saving} disableOnSpin>Update</ActionButton>
            <Button variant="secondary" className="mr-2" onClick={() => this.handleCancel()}>Cancel</Button>
          </Modal.Footer>
        </Modal.Dialog>
      </AppPageWrapper >
    )
  }
}

function mapStateToProps(state: AppState): LabResultsReportingModalAuthProps {
  return {
    isPortalAdmin: isPortalAdmin(state),
    isSiteAdmin: isSiteAdmin(state),
    UserName: getUserNameForSelectedCompany(state),
    Portal: GetSelectedPortal(state)
  }
}
const ConnectedLabResultsReportingModal = connect(mapStateToProps, null)(LabResultsReportingModal)
export default ConnectedLabResultsReportingModal